import { connectorOAuthApi } from "../api";

export interface OAuthUrlArgs {
  /** Catalog slug — "salesforce" today; kept generic for future OAuth connectors. */
  catalogKey: string;
  /** Name to give the connection the callback will create. */
  connectionName: string;
  /** Consent against the sandbox (test.salesforce.com) instead of production. */
  isSandbox?: boolean;
  /** "popup" makes the backend callback return a self-closing page. */
  mode?: "popup" | "redirect";
  /** Google Sheets-specific: the pasted sheet URL when OAuth was triggered
   *  from the "Link to Google sheet" field rather than the button directly —
   *  carried through so the resulting connection scopes to just this one
   *  file. Ignored by connectors with no such concept. */
  spreadsheetUrl?: string;
  /** Google Sheets-specific: "import_once" | "live_sync". Ignored by
   *  connectors with no such concept. */
  connectionMode?: string;
}

/**
 * Fetch the provider authorize URL from the backend. Pure data — the caller
 * decides how to open it (full-page navigation or a popup window). The backend
 * callback mints and persists the refresh_token server-side, so no token ever
 * reaches the client.
 */
export const fetchAuthorizeUrl = async ({
  catalogKey,
  connectionName,
  isSandbox = false,
  mode = "redirect",
  spreadsheetUrl,
  connectionMode,
}: OAuthUrlArgs): Promise<string> => {
  const { authorize_url } = await connectorOAuthApi.authorize(catalogKey, {
    connection_name: connectionName,
    is_sandbox: isSandbox,
    mode,
    ...(spreadsheetUrl ? { spreadsheet_url: spreadsheetUrl } : {}),
    ...(connectionMode ? { connection_mode: connectionMode } : {}),
  });
  return authorize_url;
};

/**
 * Full-page OAuth: fetch the authorize URL and navigate the whole tab to it.
 * Used as the fallback when a popup is blocked. `window.location.assign` (not
 * `href =`) keeps the navigation spy-able in tests.
 */
export const beginConnectorOAuth = async (
  args: Omit<OAuthUrlArgs, "mode">,
): Promise<void> => {
  const url = await fetchAuthorizeUrl({ ...args, mode: "redirect" });
  window.location.assign(url);
};
